import { Controller } from "@hotwired/stimulus";

// Cloudflare Turnstile ウィジェットを明示レンダリング（render=explicit）する Stimulus コントローラ。
// Turbo Frame / Turbo Stream による差し替え時も connect/disconnect で再初期化・破棄するため、
// ログイン画面のように turbo_stream で再描画される箇所でも安全に動作する。
// スクリプトURLは Settings.turnstile.widget_script_url から data-turnstile-src-value として渡される。

// Cloudflareへの再検証間隔。
const RETRY_INTERVAL = 3000;

// 再検証1回あたりにかかるチャレンジ処理時間（実測で約5秒）。
const RETRY_CHALLENGE_DURATION = 5000;

// サイトキー不正・ドメイン未認可等リトライされないエラーでは error-callback が
// 3回目以降呼ばれず中立表示のまま滞留するため、上限時間で標準表示へ戻す。
// 中立表示は2回目の失敗まで継続するため、実効の再検証間隔（RETRY_INTERVAL に
// チャレンジ処理時間を加えた値）の2回分に余裕を加えた値にしている。
const VERIFYING_TIMEOUT = (RETRY_INTERVAL + RETRY_CHALLENGE_DURATION) * 2 + 10000;

export default class extends Controller {
  static values = {
    sitekey: String,
    src: String,
    loadTimeout: { type: Number, default: 10000 },
    // 文言はロケール（views.sessions.turnstile.load_failed）を単一の定義元とし、
    // ERB から data-turnstile-load-error-message-value で渡す。
    loadErrorMessage: String,
    // 文言はロケール（views.sessions.turnstile.verifying）を単一の定義元とし、
    // ERB から data-turnstile-verifying-message-value で渡す。
    verifyingMessage: String,
  };

  connect() {
    this.widgetId = null;
    this.scriptTimer = null;
    // Turbo による再接続時に前回表示分のフラグが残ると、案内を出すべき場面で
    // showLoadError が早期returnしてしまうため、他の状態と同様に再初期化する。
    this.loadErrorShown = false;
    // 検証失敗回数。1・2回目は中立表示に差し替え、3回目以降は標準のエラー表示に任せる
    // （自動リトライで解消する一時的な失敗と、実際に検証がブロックされ続けている
    // 状態とを利用者が区別できるようにするため）。
    this.errorCount = 0;
    this.verifyingOverlayEl = null;
    this.verifyingTimer = null;
    this.loadScript(() => this.renderWidget());
  }

  disconnect() {
    if (this.scriptTimer) {
      clearInterval(this.scriptTimer);
      this.scriptTimer = null;
    }
    if (this.verifyingTimer) {
      clearTimeout(this.verifyingTimer);
      this.verifyingTimer = null;
    }
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
    this.widgetId = null;
  }

  renderWidget() {
    if (!window.turnstile || this.widgetId !== null) return;
    this.widgetId = window.turnstile.render(this.element, {
      sitekey: this.sitekeyValue,
      action: "login",
      language: "ja",
      "retry-interval": RETRY_INTERVAL,
      // 1・2回目の失敗はCloudflare標準のエラー表示を中立表示で覆い、Turnstile の
      // 自動リトライに任せるため false を返す。3回目以降は標準表示に戻し、
      // ハンドリング済みとして true を返す（Turnstile 側の警告出力も抑止される）。
      "error-callback": () => {
        console.warn("Turnstile error-callback: 検証に失敗しました");
        this.errorCount += 1;
        if (this.errorCount <= 2) {
          this.showVerifyingOverlay();
          return false;
        }

        this.hideVerifyingOverlay();
        return true;
      },
      // 検証成功時は中立表示を解除する（1回目失敗→自動リトライ成功のケース向け）。
      callback: () => {
        this.hideVerifyingOverlay();
      },
      // 利用者の操作を求めるチャレンジ表示に入る前に中立表示を解除する。
      // 中立表示を重ねたままでは操作できず、操作されない限り成功・失敗いずれの
      // コールバックも発火しないため、利用者が自力で復旧できない状態になる。
      "before-interactive-callback": () => {
        this.hideVerifyingOverlay();
      },
      // インタラクティブチャレンジのタイムアウトではウィジェットがリセットされるため、
      // expired-callback と同様に次の検証をあらためて1回目として扱う。
      "timeout-callback": () => {
        this.errorCount = 0;
        this.hideVerifyingOverlay();
      },
      // トークン失効時はウィジェットをリセットして再取得させる。
      // reset はウィジェットのライフサイクル再開に相当するため、次の検証をあらためて
      // 1回目として扱えるよう検証失敗回数と中立表示も戻す。
      "expired-callback": () => {
        if (this.widgetId !== null && window.turnstile) {
          window.turnstile.reset(this.widgetId);
        }
        this.errorCount = 0;
        this.hideVerifyingOverlay();
      },
    });
  }

  // api.js を一度だけ読み込み、window.turnstile が使えるようになったら onReady を呼ぶ。
  loadScript(onReady) {
    if (window.turnstile) {
      onReady();
      return;
    }
    if (!document.querySelector(`script[src="${this.srcValue}"]`)) {
      const script = document.createElement("script");
      script.src = this.srcValue;
      script.async = true;
      script.defer = true;
      // 遮断や DNS 失敗はここで即座に判明するため、上限到達を待たずに案内へ回す。
      script.onerror = () => this.showLoadError();
      document.head.appendChild(script);
    }
    // api.js が読めない状態を待ち続けるとトークンが生成されず、利用者は
    // 案内のないままログイン不能になるため上限を設ける。
    const deadline = Date.now() + this.loadTimeoutValue;
    this.scriptTimer = setInterval(() => {
      if (window.turnstile) {
        clearInterval(this.scriptTimer);
        this.scriptTimer = null;
        onReady();
      } else if (Date.now() > deadline) {
        clearInterval(this.scriptTimer);
        this.scriptTimer = null;
        this.showLoadError();
      }
    }, 200);
  }

  // トークン未送信は常にブロックされるため、案内がないと利用者が自力で復旧できない。
  showLoadError() {
    if (this.loadErrorShown) return;
    this.loadErrorShown = true;

    // 文言はロケール（views.sessions.turnstile.load_failed）から ERB 経由で渡す前提。
    // 未設定のときは空の枠を出さず、開発時に気付けるよう警告だけ残す。
    if (!this.loadErrorMessageValue) {
      console.warn("Turnstile: 案内文言（loadErrorMessage）が未設定です");
      return;
    }

    const area = document.createElement("div");
    area.className = "login-message-area";
    const message = document.createElement("p");
    message.className = "login-error-message";
    message.textContent = this.loadErrorMessageValue;
    area.appendChild(message);
    this.element.replaceChildren(area);
  }

  // 1・2回目の検証失敗時は、Cloudflare標準のエラー表示をウィジェットの上に重ねて隠す。
  // ウィジェット本体（iframe・トークン格納用input）は消さず、上に別要素を重ねるだけに
  // 留める（showLoadErrorとは異なり、裏でCloudflareの自動リトライが継続しているため）。
  showVerifyingOverlay() {
    if (this.verifyingOverlayEl) return;
    if (!this.verifyingMessageValue) {
      console.warn("Turnstile: 案内文言（verifyingMessage）が未設定です");
      return;
    }
    const overlay = document.createElement("div");
    overlay.className = "turnstile-verifying-overlay";
    overlay.setAttribute("role", "status");
    const message = document.createElement("span");
    message.textContent = this.verifyingMessageValue;
    // 検証が継続中であることを伝えるスピナーは、style.css の .loader（CSS アニメーション）を
    // 流用して画像素材を増やさない。サイズは custom.css で案内文に合わせて縮小する。
    const spinner = document.createElement("div");
    spinner.className = "loader";
    overlay.append(message, spinner);
    this.element.appendChild(overlay);
    this.verifyingOverlayEl = overlay;
    this.verifyingTimer = setTimeout(() => this.hideVerifyingOverlay(), VERIFYING_TIMEOUT);
  }

  hideVerifyingOverlay() {
    if (this.verifyingTimer) {
      clearTimeout(this.verifyingTimer);
      this.verifyingTimer = null;
    }
    if (!this.verifyingOverlayEl) return;
    this.verifyingOverlayEl.remove();
    this.verifyingOverlayEl = null;
  }
}
